import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import { Add as AddIcon, RouteOutlined as RouteIcon } from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import FleetScheduleMatrixTab from '../../components/transportation/FleetScheduleMatrixTab';
import RoutesListTab from '../../components/transportation/RoutesListTab';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useOrganizationStore } from '../../stores/organization.store';
import { resolveTransportationRoutesTab, type TransportationRoutesTab } from './transportationRoutes.helpers';

export default function TransportationRoutes() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const { canMutate } = useAuthPermissions();

  const orgId = selectedOrganization?.id;
  const canCreate = Boolean(orgId && canMutate(`recyclee.${orgId}.*.route.create`));
  const tab = resolveTransportationRoutesTab(searchParams.get('tab'));

  if (!selectedOrganization) {
    return <Alert severity="info">{t('transportation.routes.selectOrganization')}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        icon={<RouteIcon />}
        title={t('transportation.routes.title')}
        subtitle={t('transportation.routes.subtitle')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={tab === 'list' && canCreate ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/transportation/routes/new')}>
            {t('transportation.routes.addRoute')}
          </Button>
        ) : undefined}
      />

      <Paper elevation={1} sx={{ ...elevatedPanelSx, minWidth: 0 }}>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, next: TransportationRoutesTab) => setSearchParams({ tab: next }, { replace: true })}
            aria-label={t('transportation.routes.tabsLabel')}
          >
            <Tab
              value="list"
              label={t('transportation.routes.tabs.list')}
              id="routes-list-tab"
              aria-controls="routes-list-panel"
            />
            <Tab
              value="schedule"
              label={t('transportation.routes.tabs.schedule')}
              id="routes-schedule-tab"
              aria-controls="routes-schedule-panel"
            />
          </Tabs>
        </Box>
        <Divider />
        {tab === 'list' ? (
          <Box role="tabpanel" id="routes-list-panel" aria-labelledby="routes-list-tab" sx={{ p: { xs: 2, sm: 3 } }}>
            <RoutesListTab />
          </Box>
        ) : (
          <Box role="tabpanel" id="routes-schedule-panel" aria-labelledby="routes-schedule-tab" sx={{ p: { xs: 2, sm: 3 } }}>
            <FleetScheduleMatrixTab />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
